import { motion } from 'framer-motion';
import { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';

import Image from '@atoms/Image';
import HoverBox from '@atoms/HoverBox';
import WheelCarousel from './carousel';
import useFonts from '@lib/hooks/useFonts';
import { useWheelData } from '@lib/hooks/useWheelData';
import { drawWheel, colors } from './wheel-canvas.service';
import { getWheelFeatureId } from './helpers/getWheelFeatureId';
import { initiateSpin } from './helpers/wheelCanvasInitiateSpin';
import { onWheelCanvasUpdate } from './helpers/onWheelCanvasUpdate';
import { chromaPaletteParser } from '../helper/chromaPaletteParser';
import { getCommentsDetailsByFeatureId } from '@services/youtube-platform.service';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import { getWinnerFromLot } from './helpers/wheelCanvasGetWinnerFromLot';
import { TrashIcon as DeleteIcon, ChatCircleDotsIcon } from '@phosphor-icons/react';

const VISUAL_MAX = 500;

const WheelCanvas = ({
  splitDiv,
  playSound,
  layoutMode,
  stopFeature,
  playButtonSound,
  playResultSound,
  isPreview = false,
  showInstantCustomisationPopup = false,
}) => {
  const {
    type, opts, color, options, setting, streamId, metadata,
    fsWinnerId, wheelState, winnerInfo, handleSpin, showConfetti,
    setIsStarted, currentRosFeature, removeParticipant, setStartInteraction,
    setWinner, updateFSMeta, setFsWinnerId, setWinnerInfo, setWheelState, setIsExploding,
  } = useWheelData(s => ({
    type: s.type,
    opts: s.opts,
    color: s.color,
    options: s.options,
    setting: s.setting,
    streamId: s.streamId,
    metadata: s.metadata,
    fsWinnerId: s.fsWinnerId,
    wheelState: s.wheelState,
    winnerInfo: s.winnerInfo,
    handleSpin: s.handleSpin,
    showConfetti: s.showConfetti,
    setIsStarted: s.setIsStarted,
    currentRosFeature: s.currentRosFeature,
    removeParticipant: s.removeParticipant,
    setStartInteraction: s.setStartInteraction,
    setWinner: s.setWinner,
    updateFSMeta: s.updateFSMeta,
    setFsWinnerId: s.setFsWinnerId,
    setWinnerInfo: s.setWinnerInfo,
    setWheelState: s.setWheelState,
    setIsExploding: s.setIsExploding,
  }));

  const angle = useRef(0);
  const winnerIndex = useRef();
  const totalAngle = useRef(0);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationInProgressRef = useRef(false);
  const lastWinnerIndexRef = useRef(-1);

  const [radius, setRadius] = useState();
  const [animate, setanimate] = useState({});
  const [canvasSide, setCanvasSide] = useState();
  const [comments, setComments] = useState([]);
  const [isSpinHovered, setIsSpinHovered] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const entries = useMemo(
    () => [...opts, ...options].filter(
      (entry) => entry?.id != null && typeof entry.body === 'string' && entry.body.trim().length > 0
    ),
    [opts, options]
  );

  const visualGroupSize = entries.length > VISUAL_MAX ? entries.length / VISUAL_MAX : 1;

  const baseVisualEntries = useMemo(() => {
    if (entries.length <= VISUAL_MAX) return entries;
    const step = entries.length / VISUAL_MAX;
    return Array.from({ length: VISUAL_MAX }, (_, i) => entries[Math.floor(i * step)]);
  }, [entries]);

  const [activeVisualEntries, setActiveVisualEntries] = useState(baseVisualEntries);
  const wheelStateRef = useRef(wheelState);
  wheelStateRef.current = wheelState;

  useEffect(() => {
    if (wheelStateRef.current === 'spin') return;
    setActiveVisualEntries(baseVisualEntries);
  }, [baseVisualEntries]);

  const radiusOffset = layoutMode === 'square' ? 210 : 90;
  const [animationKey, setAnimationKey] = useState(0);

  let chromaColor;
  let wheelType = 'winningWheelFontSize';
  if (type === 'wheel') {
    chromaColor = color?.choiceCircleBgColor;
    wheelType = 'choiceCircleFontSize';
  } else if (type === 'newWheel') {
    chromaColor = color?.winningWheelBgColor;
    wheelType = 'winningWheelFontSize';
  }

  const { FontStyle } = setting || {};
  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(FontStyle ?? 'Sans-Serif');
  const computedFontSize = useFontSizeCalculator(
    setting?.[wheelType] ? Number(setting[wheelType]) : 1,
    1,
    ''
  );

  const newColors = useMemo(
    () => chromaPaletteParser().parse(colors, chromaColor),
    [chromaColor]
  );

  const textColor = color?.winningWheelHeadingColor || '#ffffff';

  const createWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const isResult = false;
    drawWheel(
      canvas,
      radius,
      activeVisualEntries,
      canvasSide,
      isResult,
      newColors,
      isPreview,
      fontFamily,
      computedFontSize
    );
  }, [radius, activeVisualEntries, canvasSide, newColors, isPreview, fontFamily, computedFontSize]);

  useLayoutEffect(() => {
    // Defer measurement to after ALL useLayoutEffects (including Framer Motion's AnimatePresence
    // popLayout logic that repositions the exiting motion.div to position:absolute). Without this
    // defer, both the old and new motion.div compete for height as flex-1 siblings, halving
    // #hero's clientHeight and producing the minimum 300px canvas size.
    const rafId = requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;
      const hero = el.closest('#hero') ?? el;
      const available = hero.clientHeight - radiusOffset - 90;
      setCanvasSide(Math.max(300, Math.min(available, hero.clientWidth)));
    });
    return () => cancelAnimationFrame(rafId);
  }, [radiusOffset, showInstantCustomisationPopup]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Use closest() so we always target the ancestor #hero of THIS component instance,
    // not the exiting feature's #hero when two motion.divs coexist during transition.
    const hero = el.closest('#hero') ?? el;

    const compute = () => {
      const h = containerRef.current?.closest('#hero') ?? containerRef.current;
      if (!h) return;
      const available = h.clientHeight - radiusOffset - 90;
      setCanvasSide(Math.max(300, Math.min(available, h.clientWidth)));
    };

    const ro = new ResizeObserver(compute);
    ro.observe(hero);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [radiusOffset, showInstantCustomisationPopup]);

  useEffect(() => {
    if (typeof canvasSide !== 'number' || !Number.isFinite(canvasSide)) return;
    setRadius((canvasSide / 2) - 10);
  }, [canvasSide]);

  const onComplete = useCallback(() => {
    if (wheelState !== 'spin') return;

    animationInProgressRef.current = false;
    setWheelState('ready');
    angle.current = 0;

    if (setting?.isWheelResultSoundEnabled) {
      playResultSound();
    }
    if (showConfetti && winnerInfo?.value !== null) {
      setIsExploding(true);
    }

    if (isPreview) return;
    stopFeature();
    setFsWinnerId();
    setIsStarted(false);
    setStartInteraction(false);
    const wheelIdMap = JSON.parse(localStorage.getItem('wheelIdMap') || '{}');
    delete wheelIdMap[currentRosFeature.id];
    localStorage.setItem('wheelIdMap', JSON.stringify(wheelIdMap));
  }, [
    wheelState,
    setting?.isWheelResultSoundEnabled,
    showConfetti,
    winnerInfo?.value,
    isPreview,
    playResultSound,
    setIsExploding,
    stopFeature,
    setFsWinnerId,
    setIsStarted,
    setStartInteraction,
    setWheelState
  ]);

  useEffect(() => {
    if (radius) {
      createWheel();
    }
  }, [radius, activeVisualEntries, animationKey]);

  const didPositionToWinnerRef = useRef(false);
  const hadSpinRef = useRef(false);
  useEffect(() => {
    if (wheelState === 'spin') hadSpinRef.current = true;
  }, [wheelState]);
  useEffect(() => {
    if (didPositionToWinnerRef.current || hadSpinRef.current) return;
    if (wheelState === 'spin' || !winnerInfo?.value) return;
    if (!activeVisualEntries?.length) return;
    const index = activeVisualEntries.findIndex((e) => e?.body === winnerInfo.value);
    if (index < 0) return;
    didPositionToWinnerRef.current = true;
    const sectorAngle = (2 * Math.PI) / activeVisualEntries.length;
    const winnerAngleInRadians = activeVisualEntries.length * 2 * sectorAngle - index * sectorAngle - sectorAngle / 2;
    const winnerAngleInDegrees = (winnerAngleInRadians * 180) / Math.PI;
    angle.current = winnerAngleInDegrees;
    setanimate({ rotate: winnerAngleInDegrees, transition: { duration: 0 } });
  }, [winnerInfo?.value, activeVisualEntries, wheelState]);

  useEffect(() => {
    if (wheelState !== 'spin') return;

    const result = getWinnerFromLot({
      entries,
      setting,
      newColors,
      fsWinnerId,
      winnerIndex,
      setWinner,
      updateFSMeta,
      visualGroupSize,
    });

    let spinEntries = baseVisualEntries;
    if (result && visualGroupSize > 1) {
      spinEntries = baseVisualEntries.slice();
      spinEntries[winnerIndex.current] = result.winnerEntry;
      setActiveVisualEntries(spinEntries);
    }

    initiateSpin({
      angle,
      entries: spinEntries,
      totalAngle,
      winnerIndex,
      setanimate,
    });
  }, [wheelState]);

  const getComments = useCallback(async (streamId, featureId, username) => {
    if (!featureId) return;
    const tepComments = await getCommentsDetailsByFeatureId(
      streamId,
      featureId,
      username
    );
    if (tepComments?.entity?.rows) {
      setComments(tepComments.entity.rows);
    }
  }, []);

  useEffect(() => {
    if (wheelState === 'spin') return;
    if (winnerInfo?.value === null || type !== 'newWheel') return;

    const { featureId } = getWheelFeatureId({
      streamId,
      metadata,
      wheelInteractionId: setting?.wheelInteractionId,
      wheelInteractionType: setting?.wheelInteractionType
    });

    getComments(streamId, featureId, winnerInfo?.value);
  }, [
    winnerInfo?.value,
    wheelState,
    setting?.wheelInteractionId,
    metadata?.featureId,
    type,
    streamId,
    metadata,
    setting?.wheelInteractionType,
  ]);

  useEffect(() => {
    if (wheelState === 'spin' && winnerInfo?.value) {
      playSound();
    }
  }, [winnerInfo?.value, wheelState, playSound]);

  const onUpdate = useCallback((e) => {
    onWheelCanvasUpdate(e, {
      entries: activeVisualEntries,
      newColors,
      setWinnerInfo,
      lastWinnerIndexRef,
    });
  }, [activeVisualEntries, newColors, setWinnerInfo]);

  const handleSpinClick = useCallback(() => {
    if (wheelState === 'spin' || animationInProgressRef.current) return;
    angle.current = 0;
    lastWinnerIndexRef.current = -1;
    setAnimationKey(prev => prev + 1);
    setanimate({});
    animationInProgressRef.current = true;
    handleSpin();
    if (setting?.isWheelButtonSoundEnabled) playButtonSound();
    setComments([]);
  }, [wheelState, handleSpin, setting?.isWheelButtonSoundEnabled, playButtonSound]);

  return (
    <div ref={containerRef} className="w-full h-full">
      {radius && (
        <div
          className={`flex ${layoutMode === 'square'
            ? 'flex-col-reverse justify-center'
            : 'gap-x-[7%]'} items-center w-full h-full`}
        >
          <div
            style={{
              position: 'relative',
              width: `${canvasSide}px`,
              height: `${canvasSide}px`,
            }}
          >
            <motion.canvas
              key={animationKey}
              onUpdate={onUpdate}
              animate={animate}
              onAnimationComplete={onComplete}
              initial={{ rotate: angle.current || 0 }}
              ref={canvasRef}
              width={canvasSide}
              height={canvasSide}
            />
            <img
              style={{ top: `${radius - 22}px` }}
              src="/images/wheel_marker.svg"
              className="absolute -right-26"
              alt="wheel marker"
            />

            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center pb-3">
              {entries?.length === 0 && (
                <div className="w-full mt-[140%] text-center">
                  <p className="text-grey-900 font-semibold text-3xl">
                    The wheel is empty!
                  </p>
                  <p className="text-grey-800 text-xl">
                    Participate in the chat to appear here
                  </p>
                </div>
              )}
              {wheelState === 'spin' || entries?.length === 0 ? null : (
                <motion.div
                  className="mt-4"
                  animate={isSpinHovered ? { scale: 1 } : { scale: [1, 1.08, 1] }}
                  transition={isSpinHovered
                    ? { duration: 0.2, ease: 'easeOut' }
                    : { duration: 2, ease: 'easeInOut', repeat: Infinity }
                  }
                  onHoverStart={() => setIsSpinHovered(true)}
                  onHoverEnd={() => setIsSpinHovered(false)}
                >
                  <button
                    disabled={wheelState === 'spin' || animationInProgressRef.current}
                    className={`${isPreview ? 'w-10 h-20 text-md overflow-hidden' : 'w-24 h-24 text-2xl'}
                     rounded-full border border-grey-200 hover:bg-grey-200 font-semibold text-grey-900 ${wheelState === 'spin' ? 'bg-grey-200' : 'bg-white'}
                      cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
                    onClick={handleSpinClick}
                  >
                    Spin
                  </button>
                </motion.div>
              )}
            </div>
          </div>

          {!splitDiv && (
            <div className="w-full h-full flex flex-col justify-center">
              <div
                className={`font-Poppins rounded-2xl relative px-4 py-3 text-center md:w-[55%] ${layoutMode === 'rectangle'
                  ? 'lg:w-[45%] h-fit'
                  : 'h-28 justify-center'} font-medium flex items-center`}
              >
                {winnerInfo?.value ? (
                  <>
                    {type === 'newWheel' && wheelState !== 'spin' ? (
                      <Image className="" src="/images/InteractionPage/crown.svg" />
                    ) : null}
                    <p
                      className="ml-[16px] text-4xl font-semibold max-w-full truncate-text text-left"
                      style={{ color: textColor }}
                    >
                      {winnerInfo?.value}
                    </p>
                  </>
                ) : null}
                {entries?.length > 2 &&
                  winnerInfo?.value &&
                  wheelState !== 'spin' &&
                  !isPreview ? (<div className="flex items-center">
                    {process.env.NEXT_PUBLIC_APP_ENV !== 'sandbox' ? (
                      <div>
                        <HoverBox
                          position="top"
                          variant="white"
                          id="delete-icon"
                          handleSvgClick={removeParticipant}
                          toolTipDescription="Remove participant"
                          svg={<DeleteIcon size={26} weight="bold" color="#1F1F1F" />}
                          className="group flex items-center justify-center rounded-md w-11 h-11 bg-white shadow-lg cursor-pointer ml-[16px]"
                        />
                      </div>
                    ) : null}
                    {setting?.enableWheelComments &&
                      comments?.length > 0 &&
                      layoutMode !== 'square' ?
                      (<div>
                        <HoverBox
                          position="top"
                          variant="white"
                          id="chat-toggle-icon"
                          handleSvgClick={() => setShowComments((prev) => !prev)}
                          toolTipDescription={showComments ? 'Hide comments' : 'Show comments'}
                          svg={<ChatCircleDotsIcon size={26} weight={showComments ? 'fill' : 'regular'} color="#1F1F1F" />}
                          className="group flex items-center justify-center rounded-md w-11 h-11 bg-white shadow-lg cursor-pointer ml-[16px]"
                        />
                      </div>
                      ) : null}
                  </div>) : null}
              </div>
              {setting?.enableWheelComments &&
                comments?.length > 0 &&
                wheelState !== 'spin' &&
                winnerInfo?.value &&
                layoutMode !== 'square' &&
                showComments ? (<WheelCarousel
                  textColor={textColor}
                  comments={comments}
                  metadata={metadata}
                />) : (<></>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WheelCanvas;
